import { formatRupeeAmount } from '#shared/types';

/**
 * Formatting helpers.
 *
 * Built on `Intl`, which is part of the platform, rather than a date library
 * (`moment` is ~290KB for a handful of formats).
 */

const dateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateTimeFormatter.format(date) : '—';
}

/**
 * A calendar date, with no time.
 *
 * For values that genuinely have no time of day — a reagent's expiry is the
 * whole of that day, and rendering it as "10 Sept 2026, 00:00" invites someone
 * to read midnight as meaningful.
 */
export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);
  return date ? dateFormatter.format(date) : '—';
}

const countFormatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });

/** A whole count with thousands separators: `4,096`. */
export function formatCount(value: number): string {
  return countFormatter.format(value);
}

interface NumberFormatOptions {
  /** Decimal places kept, at most. */
  maxFractionDigits?: number | undefined;
  /** Show `+` on increases, for a ledger of movements. */
  signed?: boolean | undefined;
}

const numberFormatters = new Map<string, Intl.NumberFormat>();

/** A measured amount — stock, milliseconds: `1,250.5`, or `+12` in a ledger. */
export function formatNumber(
  value: number,
  { maxFractionDigits = 2, signed = false }: NumberFormatOptions = {},
): string {
  const cacheKey = `${maxFractionDigits}:${signed}`;
  let formatter = numberFormatters.get(cacheKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-GB', {
      maximumFractionDigits: maxFractionDigits,
      signDisplay: signed ? 'always' : 'auto',
    });
    numberFormatters.set(cacheKey, formatter);
  }
  return formatter.format(value);
}

/**
 * A stock quantity in its base unit, at the four places the ledger stores —
 * fewer would show a recipe of 0.0005 mL as nothing at all.
 */
export function formatBaseQuantity(value: number): string {
  return formatNumber(value, { maxFractionDigits: 4 });
}

/** Money, always two decimals. Values arrive as numbers from the API. */
export function formatMoney(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `Rs ${formatRupeeAmount(value)}`;
}

/** `34 years, 2 months, 10 days`, skipping zero components. */
export function formatAge(years: number, months: number, days: number): string {
  const parts: string[] = [];
  if (years) parts.push(countOf(years, 'year'));
  if (months) parts.push(countOf(months, 'month'));
  if (days) parts.push(countOf(days, 'day'));
  return parts.length > 0 ? parts.join(', ') : '0 days';
}

/**
 * A lab test's committed turnaround.
 *
 * `6 -> "6 hours"` · `24 -> "1 day"` · `72 -> "3 days"` · `null -> "No TAT"`.
 *
 * "No TAT" is deliberately not "—": a test with no committed turnaround is a
 * configuration gap the lab may want to close, not merely a blank.
 */
export function formatTat(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return 'No TAT';
  if (hours < 24) return countOf(hours, 'hour');
  const days = hours / 24;
  return Number.isInteger(days) ? countOf(days, 'day') : `${hours} hours`;
}

/** Signed hours until `due_at`. Negative means overdue. Null when there is no TAT. */
function hoursUntil(dueAt: string | Date | null | undefined): number | null {
  const date = toDate(dueAt);
  return date === null ? null : (date.getTime() - Date.now()) / 3_600_000;
}

type DueTone = 'overdue' | 'soon' | 'ok' | 'none';

/**
 * How a due date should read in a worklist row.
 *
 * Returned as a tone plus a label so the caller picks the badge colour from
 * the semantic tokens rather than deciding thresholds itself — the worklist
 * and the dashboard must agree on what "soon" means.
 */
export function describeDue(dueAt: string | Date | null | undefined): {
  tone: DueTone;
  label: string;
} {
  const hours = hoursUntil(dueAt);
  if (hours === null) return { tone: 'none', label: 'No TAT' };

  if (hours < 0) {
    const overdue = Math.abs(hours);
    return {
      tone: 'overdue',
      label: overdue < 1 ? 'Just overdue' : `${formatDuration(overdue)} overdue`,
    };
  }

  return {
    tone: hours <= 2 ? 'soon' : 'ok',
    label: hours < 1 ? 'Due within the hour' : `Due in ${formatDuration(hours)}`,
  };
}

/** `1.5 -> "1h"` · `30 -> "1d"` · `50 -> "2d"`. Compact, for a table cell. */
function formatDuration(hours: number): string {
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * Compact age for a table cell: `69y 2m`, `4m 18d`, `18d`.
 *
 * The long form ("69 years, 2 months, 1 day") is right on a detail screen but
 * far too wide in a column — it was wrapping rows to four lines, which silently
 * defeats the fixed row height the density setting controls.
 *
 * Only the two largest non-zero units are shown; below a year the days matter,
 * above it they do not.
 */
export function formatAgeShort(years: number, months: number, days: number): string {
  const parts: string[] = [];
  if (years) parts.push(`${years}y`);
  if (months) parts.push(`${months}m`);
  // Days are noise next to a year, but they are the whole story for a newborn.
  if (days && years === 0) parts.push(`${days}d`);

  return parts.slice(0, 2).join(' ') || '0d';
}

/** The placeholder for a value that is not set, app-wide. */
const EM_DASH = '—';

/**
 * Renders `—` for null, undefined and the empty string alike.
 *
 * Deliberately not falsy-based: 0 and 0.00 are real values, and an age of 0
 * must print "0", not a dash. That distinction is why call sites had drifted
 * into a mix of `|| '—'` (which catches "") and `?? '—'` (which does not).
 */
export function dash(value: string | number | null | undefined): string {
  return value === null || value === undefined || value === '' ? EM_DASH : String(value);
}

/** A change as text, with a `+` in front of an increase: `+12%`, `-3`, `0`. */
export function signed(value: number, formatted: string): string {
  return `${value > 0 ? '+' : ''}${formatted}`;
}

/** `1 reagent`, `3 reagents` — the count and the noun that agrees with it. */
export function countOf(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Comma-joins the values that are present, or `—` when none are. */
export function joinList(values: readonly (string | null | undefined)[]): string {
  const present = values.filter((value): value is string => Boolean(value));
  return present.length > 0 ? present.join(', ') : EM_DASH;
}
